// src/pages/app/BotListPage.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, fetchBots, deleteBot } from "../../api/bots";
import { useTranslation } from "react-i18next";

const BotsListPage: React.FC = () => {
  const { t } = useTranslation();
  const [bots, setBots] = useState<Bot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchBots()
      .then((data) => setBots(data))
      .catch((err: any) => {
        console.error(err);
        setError(err?.message || t("botsList.errors.loadFailed"));
      })
      .finally(() => setLoading(false));
  }, [t]);

  const handleDeleteBot = async (bot: Bot) => {
    const slugInput = window.prompt(
      t("botsList.confirm.typeSlug", { slug: bot.slug })
    );
    if (!slugInput) return;

    if (slugInput !== bot.slug) {
      window.alert(t("botsList.confirm.slugMismatch"));
      return;
    }

    const ok = window.confirm(
      t("botsList.confirm.deleteBot", { name: bot.name })
    );
    if (!ok) return;

    try {
      await deleteBot(bot.id, bot.slug);
      setBots((prev) => prev.filter((b) => b.id !== bot.id));
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botsList.errors.deleteFailed"));
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>{t("botsList.title")}</h1>
        <Link className="btn-primary" to="/onboarding/bots/new">
          {t("botsList.actions.create")}
        </Link>
      </div>

      {loading && <p>{t("botsList.loading")}</p>}
      {error && <div className="form-error">{error}</div>}

      {!loading && !error && bots.length === 0 && (
        <p>{t("botsList.empty")}</p>
      )}

      {!loading && !error && bots.length > 0 && (
        <div className="table-responsive">
          <table className="table bots-table">
            <thead>
              <tr>
                <th>{t("botsList.table.name")}</th>
                <th>{t("botsList.table.slug")}</th>
                <th>{t("botsList.table.status")}</th>
                <th>{t("botsList.table.domain")}</th>
                <th>{t("botsList.table.channels")}</th>
                <th>{t("botsList.table.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {bots.map((bot) => (
                <tr key={bot.id}>
                  <td data-label={t("botsList.table.name")}>
                    {bot.name}
                  </td>
                  <td data-label={t("botsList.table.slug")}>
                    {bot.slug}
                  </td>
                  <td data-label={t("botsList.table.status")}>
                    {bot.status}
                  </td>
                  <td
                    data-label={t("botsList.table.domain")}
                    className="bots-table-extra"
                  >
                    {bot.domain || "-"}
                  </td>
                  <td
                    data-label={t("botsList.table.channels")}
                    className="bots-table-extra"
                  >
                    {[
                      bot.channelWeb && t("botsList.channels.web"),
                      bot.channelWhatsapp && t("botsList.channels.whatsapp"),
                      bot.channelMessenger && t("botsList.channels.messenger"),
                      bot.channelInstagram && t("botsList.channels.instagram")
                    ]
                      .filter(Boolean)
                      .join(", ") || "-"}
                  </td>

                  <td
                    className="bots-actions-cell"
                    data-label={t("botsList.table.actions")}
                  >
                    {/* Open / view bot */}
                    <Link
                      to={`/app/bots/${bot.id}`}
                      className="icon-button icon-button-ghost"
                    >
                      <span className="sr-only">
                        {t("botsList.actions.openBot")}
                      </span>
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 20 20"
                        className="icon-svg"
                      >
                        <path
                          d="M6.25 5.5h7.25a.5.5 0 0 1 .5.5v7.25h-1.5V8.56L7.1 14.46 6.04 13.4l5.4-5.4H6.25V5.5Z"
                          fill="currentColor"
                        />
                      </svg>
                    </Link>

                    {/* Delete bot */}
                    <button
                      type="button"
                      className="icon-button icon-button-danger"
                      onClick={() => handleDeleteBot(bot)}
                    >
                      <span className="sr-only">
                        {t("botsList.actions.deleteBot")}
                      </span>
                      <svg
                        aria-hidden="true"
                        viewBox="0 0 20 20"
                        className="icon-svg"
                      >
                        <path
                          d="M7.5 4h5a.5.5 0 0 1 .5.5V5H17v1.5h-1.1l-.8 8.3A1.75 1.75 0 0 1 13.37 16H6.63A1.75 1.75 0 0 1 4.9 14.8L4.1 6.5H3V5h4v-.5a.5.5 0 0 1 .5-.5Zm-.9 4.25h1.5l-.2 5H6.4l.2-5Zm3.4 0h1.4l-.2 5h-1.4l.2-5Z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BotsListPage;
